import * as Sentry from "@sentry/react";
import { PropsWithChildren, useEffect, useRef } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import { useConfig } from "./ConfigProvider";

const BROWSER_EXTENSION_URL = /^(chrome|moz|safari-web)-extension:\/\//i;

const initSentry = (dsn: string) => {
  if (import.meta.env.MODE === "e2e" || import.meta.env.DEV) return; // disable Sentry in e2e and dev mode (localhost)

  Sentry.init({
    dsn,
    integrations: [
      Sentry.replayIntegration(),
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        ),
      }),
    ],
    // denyUrls filters errors whose top-level script URL is an extension.
    // beforeSend catches the rest: errors triggered by extension DOM
    // interference (e.g. insertBefore/removeChild) whose stacktrace includes
    // extension frames but originates from react-dom.
    denyUrls: [BROWSER_EXTENSION_URL],
    beforeSend(event) {
      const frames =
        event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ??
        [];
      if (
        frames.some((f) => f.filename && BROWSER_EXTENSION_URL.test(f.filename))
      ) {
        return null;
      }
      return event;
    },
    // Session Replay
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 1.0,
  });
};

export const SentryProvider = (props: PropsWithChildren) => {
  const { children } = props;

  const config = useConfig();
  const currentDsnRef = useRef<string | null>(null);

  useEffect(() => {
    const newDsn = config.SENTRY_DSN;

    if (newDsn !== currentDsnRef.current) {
      if (currentDsnRef.current) {
        Sentry.close(); // If DSN changed, close existing instance and reinitialize
      }

      if (newDsn) {
        initSentry(newDsn);
      }

      currentDsnRef.current = newDsn;
    }

    return () => {
      if (currentDsnRef.current) {
        Sentry.close();
        currentDsnRef.current = null;
      }
    };
  }, [config.SENTRY_DSN]);

  return <>{children}</>;
};
